import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { FixedSizeGrid as Grid } from 'react-window';

function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const ro = new ResizeObserver(() => {
      setSize({ width: el.clientWidth, height: el.clientHeight });
    });
    ro.observe(el);
    setSize({ width: el.clientWidth, height: el.clientHeight });
    return () => { ro.disconnect(); };
  }, []);
  return [ref, size] as const;
}

type Props<T> = {
  items: T[];
  columnWidth: number;
  rowHeight: number;
  gap: number;
  render: (item: T, index: number) => React.ReactNode;
  columnsOverride?: number;
};

export default function VirtualizedGrid<T>({ items, columnWidth, rowHeight, gap, render, columnsOverride }: Props<T>) {
  const [containerRef, { width: containerWidth }] = useElementSize<HTMLDivElement>();
  const columns = Math.max(1, columnsOverride || Math.floor((containerWidth - gap) / (columnWidth + gap)) || 1);
  const rows = Math.ceil(items.length / columns);

  const itemKey = useMemo(() => (params: { columnIndex: number; rowIndex: number }) => {
    const index = params.rowIndex * columns + params.columnIndex;
    return index;
  }, [columns]);

  const gridHeight = Math.min((typeof window !== 'undefined' ? window.innerHeight : 800) - 360, rows * (rowHeight + gap));

  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      {containerWidth > 0 && (
        <Grid
          columnCount={columns}
          columnWidth={columnWidth + gap}
          height={gridHeight}
          rowCount={rows}
          rowHeight={rowHeight + gap}
          width={containerWidth}
          itemKey={itemKey as any}
        >
          {({ columnIndex, rowIndex, style }) => {
            const index = rowIndex * columns + columnIndex;
            if (index >= items.length) return null;
            const item = items[index];
            const s: CSSProperties = {
              ...style,
              left: (style as any).left + gap,
              top: (style as any).top + gap,
              width: columnWidth,
              height: rowHeight,
            };
            return (
              <div style={s}>
                {render(item, index)}
              </div>
            );
          }}
        </Grid>
      )}
    </div>
  );
}


